import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useStore } from 'effector-react';
import user, { getUserGames, setPaginationUserGames } from '@stores/user';
import { Spin, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import IGame from '@interfaces/IGame';
import GameCard from '@components/GameCard';
import componentContent from './UserDetailGames.content.json';
import useTranslate from '@hooks/useTranslate';
import { pageSizeOptions } from '@constants/pagination';
import useScrollTopPage from '@hooks/useScrollTopPage';
import css from './UserDetailGames.module.less';

const UserDetailGames: FunctionComponent = () => {
  const { item, userGames, preloaders, paginationUserGames } = useStore(user);
  const content = useTranslate(componentContent);

  useEffect(() => {
    if (!item?.id) return;

    getUserGames({
      userId: item.id,
      pagination: {
        current: paginationUserGames.current,
        pageSize: paginationUserGames.pageSize,
      },
    });
  }, [item?.id, paginationUserGames.current, paginationUserGames.pageSize]);

  useScrollTopPage(paginationUserGames);

  const onChange = useCallback(
    (pagination: TablePaginationConfig) => {
      if (!item?.id) return;

      const next = {
        current: pagination.current ?? 1,
        pageSize: pagination.pageSize ?? paginationUserGames.pageSize,
      };

      setPaginationUserGames(next);

      getUserGames({
        userId: item.id,
        pagination: next,
      });
    },
    [item?.id, paginationUserGames.pageSize],
  );

  const columns: ColumnsType<IGame> = [
    { key: 'id', dataIndex: 'id', title: content.id, width: 56 },
    {
      key: 'name',
      dataIndex: 'name',
      title: content.name,
      render: (_, game) => (
        <GameCard
          game={game}
          mode='row'
        />
      ),
    },
  ];

  return (
    <div className={css.root}>
      <Spin spinning={!!preloaders.getUserGames}>
        <Table
          rowKey='id'
          dataSource={userGames}
          columns={columns}
          pagination={{
            current: paginationUserGames.current,
            pageSize: paginationUserGames.pageSize,
            total: paginationUserGames.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions,
            position: ['topRight', 'bottomRight'],
          }}
          onChange={onChange}
        />
      </Spin>
    </div>
  );
};

export default UserDetailGames;
